import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { Plus, Printer, X, Trash2, Download, Mail, Sparkles, Banknote } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import CollectPaymentDialog from "@/components/app/CollectPaymentDialog";
import BillingTabs from "@/components/app/BillingTabs";
import NewInvoiceWizard from "@/components/billing/NewInvoiceWizard";
import InvoicesTable from "@/components/billing/InvoicesTable";
import InvoiceDetailsDrawer from "@/components/billing/InvoiceDetailsDrawer";
import InvoicePreviewModal from "@/components/billing/InvoicePreviewModal";
import { useAuth } from "@/lib/auth";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Billing() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "doctor";
  const [invoices, setInvoices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [receiptId, setReceiptId] = useState(null);
  const [drawerId, setDrawerId] = useState(null);            // premium details drawer
  const [previewInvoice, setPreviewInvoice] = useState(null); // A4 document preview modal
  const [collectFor, setCollectFor] = useState(null);   // { id, name } — opens CollectPaymentDialog

  const load = useCallback(async () => {
    const [i, p, s] = await Promise.all([
      api.get("/invoices"), api.get("/patients"), api.get("/dashboard/summary"),
    ]);
    setInvoices(i.data); setPatients(p.data); setSummary(s.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadInvoicePdf = async (inv, variant = "digital") => {
    try {
      const resp = await api.get(`/invoices/${inv.id}/pdf?variant=${variant}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      const suffix = variant === "letterhead" ? "-letterhead" : "";
      a.href = url; a.download = `invoice-${inv.invoice_number || inv.id}${suffix}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("PDF download failed"); }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number || inv.id}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Invoice deleted successfully");
      load();
    } catch (err) {
      toast.error(apiError(err, "Delete failed"));
    }
  };

  const emailInvoice = async (inv) => {
    const patient = patients.find(p => p.id === inv.patient_id);
    const defaultEmail = patient?.email || "";
    const to = window.prompt(
      `Send invoice ${inv.invoice_number || ""} to which email?`,
      defaultEmail,
    );
    if (!to) return;
    const tid = toast.loading("Sending email…");
    try {
      const r = await api.post(`/invoices/${inv.id}/email`, { to });
      toast.success(`Emailed to ${r.data.to}`, { id: tid });
    } catch (err) {
      toast.error(apiError(err, "Failed to send"), { id: tid });
    }
  };

  const whatsappInvoice = async (inv) => {
    const tid = toast.loading("Sending on WhatsApp…");
    try {
      await api.post(`/invoices/${inv.id}/whatsapp`);
      toast.success("Sent on WhatsApp", { id: tid });
    } catch (err) {
      toast.error(apiError(err, "WhatsApp send failed"), { id: tid });
    }
  };

  const cancelInvoice = async (inv) => {
    if (!window.confirm(`Cancel invoice ${inv.invoice_number || inv.id}? It will be kept with a CANCELLED status and a compensating ledger entry will be created; this cannot be undone.`)) return;
    try {
      await api.put(`/invoices/${inv.id}/status`, { status: "cancelled" });
      toast.success("Invoice cancelled");
      load();
    } catch (err) {
      toast.error(apiError(err, "Cancel failed"));
    }
  };

  const duplicateInvoice = async (inv) => {
    try {
      const items = (inv.items || []).map((it) => ({ ...it }));
      await api.post("/invoices", {
        patient_id: inv.patient_id,
        items,
        discount_type: inv.discount_type || "none",
        discount_value: inv.discount_value || 0,
        gst_rate: inv.gst_rate || 0,
        place_of_supply: inv.place_of_supply || null,
        invoice_type: inv.invoice_type || "non_gst",
        status: "pending",
        notes: inv.notes || null,
      });
      toast.success("Invoice duplicated");
      load();
    } catch (err) {
      toast.error(apiError(err, "Duplicate failed"));
    }
  };

  const collected = Number(summary?.revenue_week || 0);
  const outstanding = Number(summary?.outstanding || 0);

  return (
    <div>
      <BillingTabs />
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className="overline text-ink-soft">Billing & revenue</span>
          <h1 className="mt-2 font-display text-4xl tracking-tight">The books, quietly balanced.</h1>
        </div>
        <button data-testid="btn-new-invoice" onClick={() => setOpen(true)}
                className="btn-primary text-sm inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New invoice
        </button>
        <NewInvoiceWizard
          open={open}
          onOpenChange={setOpen}
          patients={patients}
          onSaved={() => { setOpen(false); load(); }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="card-widget"><div className="overline text-ink-soft">Collected (7d)</div><div className="mt-3 font-display text-4xl">{INR(collected)}</div></div>
        <div className="card-widget"><div className="overline text-ink-soft">Outstanding</div><div className="mt-3 font-display text-4xl text-[#B94A48]">{INR(outstanding)}</div></div>
        <div className="card-widget"><div className="overline text-ink-soft">Invoices</div><div className="mt-3 font-display text-4xl">{invoices.length}</div></div>
      </div>

      <div className="card-widget mb-6">
        <div className="overline text-ink-soft mb-3">Revenue — last 7 days</div>
        <div className="h-56" data-testid="revenue-bar-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={summary?.daily_revenue || []}>
              <XAxis dataKey="day" stroke="#9A9E9C" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#9A9E9C" fontSize={12} tickLine={false} axisLine={false} width={40} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8EAE9", fontSize: 12 }} />
              <Bar dataKey="revenue" fill="#1A362B" radius={[8,8,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E8EAE9] bg-gradient-to-br from-white to-linen p-4 mb-6 text-xs text-ink-mid flex items-start gap-3" data-testid="upi-hint">
        <div className="w-8 h-8 rounded-full bg-pine text-linen grid place-items-center shrink-0 text-[10px] font-mono">UPI</div>
        <div className="flex-1">
          <div className="font-medium text-ink text-sm">Pay online supports UPI, Cards, Netbanking & Wallets.</div>
          <div className="mt-1">Powered by <strong>Razorpay</strong> (India-native). In test mode, use UPI id <code className="font-mono">success@razorpay</code> or test card <code className="font-mono">4111 1111 1111 1111</code>, any future expiry, CVV <code className="font-mono">123</code>.</div>
        </div>
      </div>

      <InvoicesTable
        invoices={invoices}
        canDelete={canDelete}
        onOpen={(inv) => setDrawerId(inv.id)}
        onPreview={(inv) => setPreviewInvoice({ id: inv.id, number: inv.invoice_number })}
        onCollect={(inv) => setCollectFor({ id: inv.patient_id, name: inv.patient_name })}
        onEmail={(inv) => emailInvoice(inv)}
        onWhatsapp={(inv) => whatsappInvoice(inv)}
        onDownloadPdf={(inv, v) => downloadInvoicePdf(inv, v || "digital")}
        onPrint={(inv) => downloadInvoicePdf(inv, "letterhead")}
        onDuplicate={(inv) => duplicateInvoice(inv)}
        onCancel={(inv) => cancelInvoice(inv)}
        onDelete={(inv) => deleteInvoice(inv)}
        onViewPatient={(inv) => (window.location.href = `/app/patients/${inv.patient_id}`)}
      />

      {receiptId && <ReceiptModal invoiceId={receiptId} onClose={() => setReceiptId(null)} />}
      <InvoicePreviewModal
        invoiceId={previewInvoice?.id}
        invoiceNumber={previewInvoice?.number}
        open={!!previewInvoice}
        onOpenChange={(v) => !v && setPreviewInvoice(null)}
      />
      <InvoiceDetailsDrawer
        invoiceId={drawerId}
        open={!!drawerId}
        onOpenChange={(v) => !v && setDrawerId(null)}
        onCollectPayment={(patient) => {
          setDrawerId(null);
          setCollectFor({ id: patient?.id, name: patient?.name });
        }}
      />
      <CollectPaymentDialog
        open={!!collectFor}
        onOpenChange={(v) => !v && setCollectFor(null)}
        patientId={collectFor?.id}
        patientName={collectFor?.name}
        onSaved={() => { setCollectFor(null); load(); }}
      />
    </div>
  );
}

// ============================================================================
// InvoiceForm — line items + discount + live GST preview
// ============================================================================
function InvoiceForm({ patients, onSaved }) {
  const [patient_id, setPatientId] = useState("");
  const [items, setItems] = useState([{ _rid: crypto.randomUUID(), name: "Consultation", amount: 500, quantity: 1 }]);
  const [discount_type, setDiscType] = useState("none");
  const [discount_value, setDiscValue] = useState(0);
  const [gst_rate, setGstRate] = useState(18);
  const [place_of_supply, setPlace] = useState("");
  const [status, setStatus] = useState("pending");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [clinic, setClinic] = useState(null);

  useEffect(() => {
    api.get("/clinics/me").then((r) => setClinic(r.data)).catch(() => {});
  }, []);

  const isGstMode = (clinic?.invoice_type || (clinic?.gstin ? "gst" : "non_gst")) === "gst";

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + (Number(it.amount) || 0) * (Number(it.quantity) || 1), 0);
    const disc = discount_type === "percentage" ? subtotal * (Number(discount_value) / 100)
              : discount_type === "fixed" ? Math.min(Number(discount_value) || 0, subtotal) : 0;
    const taxable = subtotal - disc;
    const effectiveRate = isGstMode ? Number(gst_rate) : 0;
    const totalGst = taxable * (effectiveRate / 100);
    const intra = !place_of_supply || (clinic?.state || "").toLowerCase() === place_of_supply.toLowerCase();
    const cgst = intra ? totalGst / 2 : 0;
    const sgst = intra ? totalGst / 2 : 0;
    const igst = intra ? 0 : totalGst;
    return { subtotal, disc, taxable, cgst, sgst, igst, total: taxable + cgst + sgst + igst };
  }, [items, discount_type, discount_value, gst_rate, place_of_supply, clinic, isGstMode]);

  const updateItem = (idx, field, val) => setItems((p) => p.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  const addItem = () => setItems((p) => [...p, { _rid: crypto.randomUUID(), name: "", amount: 0, quantity: 1 }]);
  const removeItem = (idx) => setItems((p) => p.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!patient_id) return toast.error("Select a patient");
    setBusy(true);
    try {
      await api.post("/invoices", {
        patient_id, items,
        discount_type, discount_value: Number(discount_value),
        gst_rate: isGstMode ? Number(gst_rate) : 0,
        place_of_supply: isGstMode ? place_of_supply : null,
        invoice_type: isGstMode ? "gst" : "non_gst",
        status, notes,
      });
      toast.success("Invoice created");
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err, "Failed"));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="new-invoice-form">
      <select required data-testid="inv-patient" value={patient_id} onChange={(e) => setPatientId(e.target.value)}
        className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
        <option value="">Select patient</option>
        {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>

      <div>
        <div className="overline text-ink-soft mb-2">Line items</div>
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={it._rid || idx} className="grid grid-cols-12 gap-2" data-testid={`inv-item-${idx}`}>
              <input required placeholder="Description" value={it.name}
                onChange={(e) => updateItem(idx, "name", e.target.value)}
                className="col-span-6 rounded-lg border border-[#E8EAE9] px-3 py-2 text-sm" />
              <input required type="number" min={1} placeholder="Qty" value={it.quantity}
                onChange={(e) => updateItem(idx, "quantity", Number(e.target.value))}
                className="col-span-2 rounded-lg border border-[#E8EAE9] px-3 py-2 text-sm" />
              <input required type="number" min={0} placeholder="Amount (₹)" value={it.amount}
                onChange={(e) => updateItem(idx, "amount", Number(e.target.value))}
                className="col-span-3 rounded-lg border border-[#E8EAE9] px-3 py-2 text-sm" />
              <button type="button" onClick={() => removeItem(idx)} disabled={items.length === 1}
                className="col-span-1 text-ink-soft hover:text-[#B94A48] disabled:opacity-30">
                <Trash2 className="w-4 h-4 mx-auto" />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addItem} data-testid="inv-add-item"
          className="mt-2 text-xs text-ink hover:underline inline-flex items-center gap-1">
          <Plus className="w-3 h-3" /> Add item
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <select data-testid="inv-disc-type" value={discount_type} onChange={(e) => setDiscType(e.target.value)}
          className="rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm bg-white">
          <option value="none">No discount</option>
          <option value="percentage">Discount %</option>
          <option value="fixed">Flat ₹ off</option>
        </select>
        <input type="number" min={0} disabled={discount_type === "none"} placeholder="0"
          data-testid="inv-disc-value" value={discount_value} onChange={(e) => setDiscValue(Number(e.target.value))}
          className="rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm disabled:bg-linen disabled:text-ink-soft" />
        {isGstMode ? (
          <input type="number" min={0} max={28} step={0.5} placeholder="GST %"
            data-testid="inv-gst-rate" value={gst_rate} onChange={(e) => setGstRate(Number(e.target.value))}
            className="rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm" />
        ) : (
          <div className="rounded-xl border border-dashed border-[#E8EAE9] px-3 py-2 text-xs text-ink-soft grid place-items-center">
            Non-GST invoice
          </div>
        )}
      </div>

      {isGstMode && (
        <input placeholder="Place of supply (patient state, e.g. Maharashtra)"
          data-testid="inv-place" value={place_of_supply} onChange={(e) => setPlace(e.target.value)}
          className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
      )}

      <select data-testid="inv-status" value={status} onChange={(e) => setStatus(e.target.value)}
        className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
        <option value="pending">Pending</option>
        <option value="overdue">Overdue</option>
      </select>
      <p className="text-[11px] text-ink-soft -mt-2">
        New invoices always start as <b>Pending</b>. Use <b>Collect payment</b> after creating the invoice to record cash / UPI / card / mixed payments — this keeps the ledger, dashboards and account summary in sync.
      </p>

      {/* Live totals */}
      <div className="rounded-2xl bg-linen p-4 space-y-1 text-sm" data-testid="inv-live-totals">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-ink-soft mb-1">
          <span>{isGstMode ? "GST Invoice preview" : "Non-GST Invoice preview"}</span>
          <span>{clinic?.gstin && isGstMode ? `GSTIN: ${clinic.gstin}` : ""}</span>
        </div>
        <Row label="Subtotal" v={INR(totals.subtotal)} />
        {totals.disc > 0 && <Row label={`Discount (${discount_type === "percentage" ? `${discount_value}%` : "flat"})`} v={`- ${INR(totals.disc)}`} muted />}
        {isGstMode && <Row label="Taxable amount" v={INR(totals.taxable)} muted />}
        {isGstMode && totals.cgst > 0 && <Row label={`CGST @ ${gst_rate / 2}%`} v={INR(totals.cgst)} muted />}
        {isGstMode && totals.sgst > 0 && <Row label={`SGST @ ${gst_rate / 2}%`} v={INR(totals.sgst)} muted />}
        {isGstMode && totals.igst > 0 && <Row label={`IGST @ ${gst_rate}%`} v={INR(totals.igst)} muted />}
        <div className="pt-2 mt-2 border-t border-[#E8EAE9] flex justify-between">
          <span className="font-display text-lg">Total</span>
          <span className="font-display text-2xl">{INR(totals.total)}</span>
        </div>
      </div>

      <button data-testid="inv-submit" disabled={busy} className="w-full btn-primary text-sm">
        {busy ? "Saving…" : "Create invoice"}
      </button>
    </form>
  );
}

function Row({ label, v, muted }) {
  return (
    <div className={`flex justify-between ${muted ? "text-ink-mid" : ""}`}>
      <span>{label}</span><span>{v}</span>
    </div>
  );
}

// ============================================================================

function AccountSummarySection({ summary, tpl }) {
  const prev = Number(summary?.previous_outstanding || 0);
  const today = Number(summary?.todays_charges || 0);
  const paid = Number(summary?.payment_received || 0);
  const wallet = Number(summary?.wallet_applied || 0);
  const current = Number(summary?.current_outstanding || 0);
  const status = summary?.payment_status || "unpaid";
  const statusPretty = {
    fully_paid: "Fully Paid", partially_paid: "Partially Paid",
    unpaid: "Unpaid", cancelled: "Cancelled",
  }[status] || status;
  const currentOk = current <= 0.01;

  const showPrev   = tpl.show_previous_outstanding !== false;
  const showCurr   = tpl.show_current_outstanding !== false;
  const showWallet = tpl.show_wallet_usage !== false;
  const showStatus = tpl.show_payment_status !== false;

  return (
    <section className="mt-6 rounded-2xl bg-linen border border-[#E7E1D3] p-4"
             data-testid="account-summary-section">
      <div className="overline text-ink-soft mb-2">Patient Account Summary</div>
      <div className="space-y-1 text-sm">
        {showPrev && (
          <div className="flex justify-between" data-testid="acct-prev">
            <span className="text-ink-mid">Previous Outstanding</span>
            <span className="font-mono">{INR(prev)}</span>
          </div>
        )}
        <div className="flex justify-between" data-testid="acct-today">
          <span className="text-ink-mid">Today's Charges</span>
          <span className="font-mono">{INR(today)}</span>
        </div>
        <div className="flex justify-between" data-testid="acct-paid">
          <span className="text-ink-mid">Payment Received Today</span>
          <span className="font-mono">{INR(paid)}</span>
        </div>
        {showWallet && wallet > 0 && (
          <div className="flex justify-between text-[#2D6A4F]" data-testid="acct-wallet">
            <span>Wallet Applied</span>
            <span className="font-mono">−{INR(wallet)}</span>
          </div>
        )}
      </div>

      {showCurr && (
        <div
          data-testid="acct-current"
          className={`mt-3 flex justify-between items-center p-3 rounded-xl border-2 ${
            currentOk
              ? "bg-[#E8F0EB] border-[#B8D6C2] text-[#2D6A4F]"
              : "bg-[#FFF4E5] border-[#F2C494] text-[#8A4B00]"
          }`}
        >
          <span className="font-semibold">Current Outstanding</span>
          <span className="font-display text-xl">{INR(current)}</span>
        </div>
      )}

      {showStatus && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {currentOk ? (
            <span data-testid="acct-badge-ok" className="inline-block px-3 py-1 rounded-full bg-[#E8F0EB] text-[#2D6A4F] text-xs font-semibold">
              ✓ No Outstanding Balance
            </span>
          ) : (
            <span data-testid="acct-badge-warn" className="inline-block px-3 py-1 rounded-full bg-[#FFF4E5] text-[#8A4B00] text-xs font-semibold">
              Outstanding Balance: {INR(current)}
            </span>
          )}
          <span className="text-xs text-ink-mid">Status: <b className="text-ink">{statusPretty}</b></span>
        </div>
      )}
    </section>
  );
}

function ReceiptModal({ invoiceId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/invoices/${invoiceId}/receipt`).then((r) => setData(r.data));
  }, [invoiceId]);
  if (!data) return null;
  const { invoice, patient, clinic, account_summary } = data;
  const isGst = invoice.invoice_type
    ? invoice.invoice_type === "gst"
    : Number(invoice.gst_rate || 0) > 0;
  const gstBreakdown = !isGst ? [] : (invoice.igst_amount > 0
    ? [{ label: `IGST @ ${invoice.gst_rate}%`, v: invoice.igst_amount }]
    : [
        { label: `CGST @ ${invoice.gst_rate / 2}%`, v: invoice.cgst_amount },
        { label: `SGST @ ${invoice.gst_rate / 2}%`, v: invoice.sgst_amount },
      ]);

  const tpl = clinic?.invoice_template || {};
  const showAcct = account_summary && tpl.show_account_summary !== false;

  const print = () => window.print();

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose} data-testid="receipt-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-3 border-b border-[#E8EAE9] no-print">
          <div className="font-display text-lg">Receipt</div>
          <div className="flex items-center gap-2">
            <button data-testid="receipt-print" onClick={print} className="btn-primary text-xs inline-flex items-center gap-1 py-2">
              <Printer className="w-3.5 h-3.5" /> Print / Save PDF
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-full grid place-items-center hover:bg-linen"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div id="receipt-print" className="p-8 text-ink">
          {/* Header */}
          <div className="flex justify-between items-start pb-6 border-b-2 border-pine">
            <div>
              <div className="font-display text-3xl tracking-tight text-ink">{clinic?.name}</div>
              <div className="text-xs text-ink-mid mt-1 whitespace-pre-line">{clinic?.address || "Address not set"}</div>
              <div className="text-xs text-ink-mid mt-1">
                {clinic?.phone && <>Phone: {clinic.phone} · </>}
                {clinic?.email && <>Email: {clinic.email}</>}
              </div>
              {clinic?.gstin && isGst && <div className="text-xs font-mono mt-2">GSTIN: <span className="font-bold">{clinic.gstin}</span></div>}
            </div>
            <div className="text-right">
              <div className="overline text-ink-soft">{isGst ? "Tax Invoice" : "Invoice"}</div>
              <div className="font-display text-xl mt-1">{invoice.invoice_number}</div>
              <div className="text-xs text-ink-mid mt-1">Date: {new Date(invoice.created_at).toLocaleDateString("en-IN")}</div>
              <div className="mt-3 inline-block px-3 py-1 rounded-full text-xs uppercase tracking-widest font-medium"
                style={{
                  background: invoice.status === "paid" ? "#E8F0EB"
                            : invoice.status === "partial" ? "#FFF4E5"
                            : invoice.status === "cancelled" ? "#EFEEEA"
                            : "#FDF3ED",
                  color:      invoice.status === "paid" ? "#2D6A4F"
                            : invoice.status === "partial" ? "#8A4B00"
                            : invoice.status === "cancelled" ? "#6E6D69"
                            : "#8A3F1E",
                }}>
                {invoice.status === "partial" ? "partially paid" : invoice.status}
              </div>
            </div>
          </div>

          {/* Parties */}
          <div className="grid grid-cols-2 gap-8 py-6 border-b border-[#E8EAE9]">
            <div>
              <div className="overline text-ink-soft mb-2">Billed to</div>
              <div className="font-display text-lg">{patient?.name}</div>
              <div className="text-xs text-ink-mid mt-1">{patient?.phone}</div>
              {patient?.email && <div className="text-xs text-ink-mid">{patient.email}</div>}
              {patient?.address && <div className="text-xs text-ink-mid mt-1">{patient.address}</div>}
            </div>
            <div>
              {isGst && (
                <>
                  <div className="overline text-ink-soft mb-2">Place of supply</div>
                  <div className="text-sm">{invoice.place_of_supply || clinic?.state || "—"}</div>
                </>
              )}
              {invoice.due_date && (<>
                <div className={`overline text-ink-soft ${isGst ? "mt-4" : ""} mb-2`}>Due date</div>
                <div className="text-sm">{new Date(invoice.due_date).toLocaleDateString("en-IN")}</div>
              </>)}
            </div>
          </div>

          {/* Items */}
          <table className="w-full mt-6 text-sm">
            <thead>
              <tr className="text-left text-ink-mid border-b border-[#E8EAE9]">
                <th className="py-2 w-8">#</th>
                <th>Description</th>
                <th className="text-right w-16">Qty</th>
                <th className="text-right w-28">Rate</th>
                <th className="text-right w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((it, i) => (
                <tr key={`inv-line-${i}-${it.name}`} className="border-b border-[#F1F2F0]">
                  <td className="py-2">{i + 1}</td>
                  <td>{it.name}</td>
                  <td className="text-right">{it.quantity || 1}</td>
                  <td className="text-right font-mono">{INR(it.amount)}</td>
                  <td className="text-right font-mono">{INR((it.amount || 0) * (it.quantity || 1))}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mt-4">
            <div className="w-full max-w-xs space-y-1 text-sm">
              <Row label="Subtotal" v={INR(invoice.subtotal)} />
              {invoice.discount_amount > 0 && (
                <Row label={`Discount (${invoice.discount_type === "percentage" ? `${invoice.discount_value}%` : "flat"})`} v={`- ${INR(invoice.discount_amount)}`} muted />
              )}
              {isGst && <Row label="Taxable amount" v={INR(invoice.taxable_amount)} muted />}
              {gstBreakdown.map((g) => (g.v > 0 ? <Row key={g.label} label={g.label} v={INR(g.v)} muted /> : null))}
              <div className="pt-2 mt-2 border-t-2 border-pine flex justify-between">
                <span className="font-display text-lg">Total payable</span>
                <span className="font-display text-2xl">{INR(invoice.total)}</span>
              </div>
            </div>
          </div>

          {invoice.notes && (
            <div className="mt-6 text-xs text-ink-mid">
              <div className="overline text-ink-soft mb-1">Notes</div>
              {invoice.notes}
            </div>
          )}

          {showAcct && (
            <AccountSummarySection
              summary={account_summary}
              tpl={tpl}
            />
          )}

          <div className="mt-10 pt-6 border-t border-[#E8EAE9] grid grid-cols-2 gap-6 text-xs text-ink-mid">
            <div>
              <div className="font-medium">Terms & conditions</div>
              <ul className="mt-1 list-disc list-inside space-y-0.5">
                <li>Payment is due within the mentioned date.</li>
                <li>All disputes subject to jurisdiction of the clinic's registered state.</li>
                <li>This is a computer-generated invoice{isGst ? " under Rule 46 of the CGST Rules, 2017" : ""}.</li>
                <li>All payments are final and non-refundable. Please verify your treatment plan and charges before making payment.</li>
              </ul>
            </div>
            <div className="text-right">
              <div className="h-12" />
              <div className="border-t border-pine/40 pt-1">Authorised signatory</div>
              <div className="text-ink-soft mt-1">{clinic?.name}</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #receipt-print, #receipt-print * { visibility: visible; }
          #receipt-print { position: absolute; left: 0; top: 0; width: 100%; padding: 24px; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}
